import { type ReactNode } from 'react';
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { toEpochMs, toIso } from '@/lib/clock';
import { formatDateTimeCompact, formatTime } from '@/lib/datetime';
import {
  compareDecimalStrings,
  formatCompactAmount,
  formatDecimalString,
  parseDecimalString,
} from '@/lib/money';

/**
 * Every settled deposit as one mark: when it arrived, and for how much.
 *
 * The histogram beside this chart already answers "which amounts?" and answers it better than a
 * scatter could — bars are a far more precise magnitude channel than position-plus-overlap. What
 * it cannot answer is "when?", because bucketing throws the clock away: a window in which every
 * large deposit arrived inside one ten-minute burst and a window in which the same deposits
 * trickled in evenly produce *identical bars*. Those are different facts about a payment book —
 * one is a payroll run or a promotion, the other is ordinary trade — and this chart exists to
 * separate them. It is a companion to the histogram, never a replacement.
 *
 * It follows the five rules in `charts.tsx` (theme tokens never colour literals, quiet axes, a
 * text equivalent for every chart, one styled tooltip surface, every word from the caller). Four
 * decisions are specific to this chart:
 *
 * 1. **The x axis is a real number line, bounded by the window — not a category axis.** The window
 *    comes in as `rangeStart`/`rangeEnd` and *is* the domain, so a quiet hour renders as empty
 *    space of the correct width. Spacing marks by their index instead, the way a category axis
 *    would, is the single most common way a time scatter lies: it silently converts "nothing
 *    happened for four hours" into "the next deposit", and a burst then looks exactly like a
 *    trickle. It also means the plot spans the whole window even when every mark sits in one
 *    corner of it, which is itself the reading.
 *
 * 2. **Overlap is the density channel, so the marks are translucent and small.** At a few thousand
 *    marks the dense band near the bottom is solid ink whatever we do; drawing each mark at
 *    partial opacity turns that from a smear into a gradient, where a doubly-stacked point is
 *    visibly darker than a lone one. This deliberately inverts the usual "2px surface ring on
 *    overlapping marks" rule, which is written for a handful of colliding marks and would, at this
 *    count, replace the data with a mesh of rings. For the same reason the marks are ~7px rather
 *    than the 8px floor: past that size the band stops resolving. What that costs is a hover
 *    target smaller than a pointer deserves, which is why the text equivalent below is not
 *    optional — it is where the values live for anyone who cannot hit a 7px circle.
 *
 * 3. **One series, so no legend and no second hue.** There is exactly one population here
 *    (deposits that settled in the window) and the card's own title names it. A legend box for a
 *    single series is furniture; a second colour would assert a distinction the data does not have.
 *
 * 4. **The y axis starts at zero and is linear.** A log axis would spread the dense band out and
 *    is genuinely tempting for a right-skewed book — but it also flattens the outliers this chart
 *    is best at showing, and it asks an operator setting a limit to read a distance as a ratio.
 *    The histogram next to it already carries semi-log bucket edges (`AMOUNT_BUCKET_EDGES`) and
 *    answers the "resolve the dense mass" question properly. Two charts, two jobs.
 */

/**
 * Rows in the text equivalent — the window cut into this many equal slices.
 *
 * The table cannot be one row per mark: there are up to two thousand of them, and a screen-reader
 * table of two thousand rows is not an accommodation, it is a denial of service. Twelve slices is
 * enough to carry the shape a sighted reader gets from the plot (where the marks bunch, where the
 * window is empty, where the largest deposit landed) in a table that can actually be read through.
 */
const TABLE_SLICES = 12;

/** Ticks along the time axis. Six labels is what fits at this width without rotating any of them. */
const AXIS_TICKS = 6;

/** Above this span the axis labels carry a date; below it, everything is the same day or two. */
const LONG_WINDOW_MS = 36 * 60 * 60 * 1000;

/** Mark area in px², handed to Recharts through `ZAxis`. 36 px² is a ~6.8 px circle — see rule 2. */
const MARK_AREA = 36;

/** One settled deposit as it arrives from the wire. */
export interface AmountScatterDatum {
  /** ISO-8601 instant the deposit was created. */
  at: string;
  /** The deposit's amount as a decimal string. Never a `number` — see {@link toPlotValue}. */
  amount: string;
}

/**
 * Every piece of text this chart renders, already translated.
 *
 * Nothing under `components/charts` calls `useTranslation` (rule 5 in `charts.tsx`): the words
 * arrive finished, which keeps the chart renderable from a test or a future PDF path where no i18n
 * provider is mounted, and keeps the locale keys beside the page that owns the feature.
 */
export interface AmountScatterLabels {
  /** Tooltip row and table column for the instant, e.g. "Time". */
  time: string;
  /** Tooltip row and table column for the money, e.g. "Amount". */
  amount: string;
  /** Table column for how many marks fall in a slice, e.g. "Deposits shown". */
  deposits: string;
  /** Table column for the slice's largest amount, e.g. "Largest". */
  largest: string;
  /** Shown in place of the plot when there is nothing to draw. */
  empty: string;
}

/** Props for {@link AmountScatter}. */
export interface AmountScatterProps {
  /** The deposits to plot, in any order — this component does not assume one. */
  data: readonly AmountScatterDatum[];
  /** Inclusive start of the window, ISO. The x axis' left edge, whether or not a mark sits on it. */
  rangeStart: string;
  /** Exclusive end of the window, ISO. The x axis' right edge. */
  rangeEnd: string;
  /** ISO currency code, for formatting amounts. */
  currency: string;
  /** Accessible description. */
  description: string;
  /** Translated chart text. Required, so a new call site cannot silently ship English. */
  labels: AmountScatterLabels;
  /** Chart height. */
  height?: number | undefined;
}

/** A deposit with the derived values the chart plots, plus the exact string it displays. */
interface Mark {
  /** Milliseconds since the epoch — the x position. */
  atMs: number;
  /** The original ISO instant, for formatting. */
  at: string;
  /** The amount as a float — the y position, and the only lossy value here. */
  amount: number;
  /** The amount as it came off the wire, exact. Everything an operator *reads* comes from this. */
  amountText: string;
}

/**
 * Turn a wire amount into the `number` the y axis plots. The one lossy step in this file.
 *
 * A pixel offset is a float and there is no honest way around that. What this buys is *where* the
 * loss happens: the decimal string is parsed exactly into a scaled `bigint` first, so a malformed
 * amount throws instead of quietly becoming `NaN` half way up the axis, and the float never leaves
 * this module — the tooltip and the table below both render `amountText`, which is the untouched
 * wire string. That is why the numbers an operator reads off this chart are exact even though the
 * dot positions are not.
 */
function toPlotValue(amount: string): number {
  const { units, scale } = parseDecimalString(amount);
  return Number(units) / 10 ** scale;
}

/**
 * Build the plottable marks, dropping anything that cannot be positioned.
 *
 * An unparseable instant would land at `NaN` on the x axis, which Recharts renders by quietly
 * collapsing the whole domain — one bad row would empty the plot rather than lose itself. Amounts
 * are left to throw: a malformed amount is a money bug and must not be swallowed by a chart.
 */
function buildMarks(data: readonly AmountScatterDatum[]): Mark[] {
  const marks: Mark[] = [];
  for (const point of data) {
    const atMs = toEpochMs(point.at);
    if (!Number.isFinite(atMs)) continue;
    marks.push({ atMs, at: point.at, amount: toPlotValue(point.amount), amountText: point.amount });
  }
  return marks.sort((a, b) => a.atMs - b.atMs);
}

/**
 * A y-axis tick label — `฿1.2k`, `฿480k`.
 *
 * Deliberately inexact, and the only place in this file that is: {@link formatCompactAmount} is
 * documented as axis-and-headline-only, and the exact amount is always one hover or one table row
 * away. The guards are for the axis, not the data — Recharts derives tick values from the domain,
 * so an empty or degenerate domain can hand this a zero or a float that `String` would render in
 * exponential notation, which the decimal parser rejects.
 */
function axisAmountLabel(value: number, currency: string): string {
  if (!Number.isFinite(value) || value <= 0) return formatCompactAmount('0', currency);
  const rounded = Math.round(value);
  return formatCompactAmount(
    rounded >= 1e21 ? BigInt(rounded).toString() : String(rounded),
    currency,
  );
}

/** Evenly spaced tick positions across `[start, end]`, both ends included. */
function axisTicks(start: number, end: number): number[] {
  if (!(end > start)) return [start];
  const step = (end - start) / (AXIS_TICKS - 1);
  return Array.from({ length: AXIS_TICKS }, (_, index) => Math.round(start + step * index));
}

/** One row of the text equivalent: a slice of the window and what landed in it. */
interface TableSlice {
  /** The slice's own label, e.g. `08:00–10:00`. */
  label: string;
  /** How many of the plotted marks fall in this slice. */
  count: number;
  /** The largest amount in the slice as an exact decimal string, or null when the slice is empty. */
  largest: string | null;
}

/**
 * Cut the window into {@link TABLE_SLICES} equal slices and summarise the marks in each.
 *
 * The slices come from the *window*, not from the data, so an empty stretch gets a row saying so —
 * which is the same claim the empty space on the plot makes, and the one a table built from the
 * marks alone could not make at all.
 *
 * `largest` is picked with {@link compareDecimalStrings} rather than by comparing the plotted
 * floats: it is the value the table prints, so it has to be the exact one.
 */
function buildSlices(
  marks: readonly Mark[],
  startMs: number,
  endMs: number,
  formatSliceTime: (ms: number) => string,
): TableSlice[] {
  const span = endMs - startMs;
  if (!(span > 0)) return [];
  const width = span / TABLE_SLICES;

  return Array.from({ length: TABLE_SLICES }, (_, index) => {
    const from = startMs + width * index;
    // The last slice takes the window's exclusive end, so a mark on the final millisecond is not
    // rounded out of every slice and lost from a table that claims to cover the whole window.
    const to = index === TABLE_SLICES - 1 ? endMs : from + width;
    const inSlice = marks.filter(
      (mark) =>
        mark.atMs >= from && (index === TABLE_SLICES - 1 ? mark.atMs <= to : mark.atMs < to),
    );
    let largest: string | null = null;
    for (const mark of inSlice) {
      if (largest === null || compareDecimalStrings(mark.amountText, largest) > 0) {
        largest = mark.amountText;
      }
    }
    return {
      label: `${formatSliceTime(from)}–${formatSliceTime(to)}`,
      count: inSlice.length,
      largest,
    };
  });
}

/** Tooltip payload row, as Recharts hands it over. */
interface TooltipEntry {
  payload?: Mark | undefined;
}

/** Tooltip for one deposit: the instant in full, and the exact amount. */
function ScatterTooltip({
  active,
  payload,
  currency,
  labels,
}: {
  active?: boolean | undefined;
  payload?: readonly TooltipEntry[] | undefined;
  currency: string;
  labels: AmountScatterLabels;
}): ReactNode {
  const mark = active ? payload?.[0]?.payload : undefined;
  if (!mark) return null;

  // The instant is spelled out to the minute here, unlike the axis ticks: a mark's position along
  // a several-hour axis is worth about an hour of precision, and the tooltip is the only place
  // that can say which minute it actually was.
  const lines: { name: string; value: string }[] = [
    { name: labels.time, value: formatDateTimeCompact(mark.at) },
    {
      name: labels.amount,
      value: formatDecimalString(mark.amountText, currency, { withSymbol: true }),
    },
  ];

  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <ul className="space-y-0.5">
        {lines.map((line) => (
          <li key={line.name} className="flex items-center gap-4 text-muted-foreground">
            <span>{line.name}</span>
            <span className="tnum ml-auto font-medium text-popover-foreground">{line.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The text equivalent, shared by the populated and empty renders. */
function ScatterTable({
  slices,
  currency,
  description,
  labels,
}: {
  slices: readonly TableSlice[];
  currency: string;
  description: string;
  labels: AmountScatterLabels;
}): ReactNode {
  return (
    // `sr-only` goes on a wrapping `div`, never on the `<table>`. See rule 6 in `charts.tsx` —
    // this table is the widest on the dashboard and was the one that broke mobile.
    <div className="sr-only">
      <table>
        <caption>{description}</caption>
        <thead>
          <tr>
            <th scope="col">{labels.time}</th>
            <th scope="col">{labels.deposits}</th>
            <th scope="col">{labels.largest}</th>
          </tr>
        </thead>
        <tbody>
          {slices.map((slice) => (
            <tr key={slice.label}>
              <th scope="row">{slice.label}</th>
              <td>{slice.count}</td>
              <td>
                {slice.largest === null
                  ? '—'
                  : formatDecimalString(slice.largest, currency, { withCode: true })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Settled deposit amounts plotted against arrival time.
 *
 * Purely presentational, like the rest of `components/charts`: the page owns the query, the window
 * and every word. See the module comment for the axis, density and colour decisions.
 */
export function AmountScatter({
  data,
  rangeStart,
  rangeEnd,
  currency,
  description,
  labels,
  height = 280,
}: AmountScatterProps): ReactNode {
  const marks = buildMarks(data);

  // The domain is the window, not the data's extent — see rule 1. A window whose bounds are
  // unparseable falls back to the marks' own range, which is wrong but drawable; an axis of `NaN`
  // is neither.
  const parsedStart = toEpochMs(rangeStart);
  const parsedEnd = toEpochMs(rangeEnd);
  const startMs = Number.isFinite(parsedStart) ? parsedStart : (marks[0]?.atMs ?? 0);
  const endMs = Number.isFinite(parsedEnd) ? parsedEnd : (marks.at(-1)?.atMs ?? startMs + 1);

  const longWindow = endMs - startMs > LONG_WINDOW_MS;
  // A short window is all one day or two, so the date on every tick would be six repetitions of
  // the same string; a long one is unreadable without it.
  const formatInstant = (ms: number): string =>
    longWindow ? formatDateTimeCompact(toIso(ms)) : formatTime(toIso(ms)).slice(0, 5);

  const slices = buildSlices(marks, startMs, endMs, formatInstant);

  if (marks.length === 0) {
    return (
      <figure className="w-full">
        <figcaption className="sr-only">{description}</figcaption>
        <div
          style={{ height }}
          className="flex items-center justify-center rounded-md border border-dashed border-border"
        >
          <p className="text-xs text-muted-foreground">{labels.empty}</p>
        </div>
      </figure>
    );
  }

  return (
    <figure className="w-full">
      <figcaption className="sr-only">{description}</figcaption>
      <div style={{ height }} role="img" aria-label={description}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            {/*
              Both directions, unlike the dashboard's other charts. On a bar chart the category
              axis needs no gridline — the bar starts at it. Here every mark floats free of both
              axes, and reading either coordinate off a bare plot means eyeballing two distances at
              once. Dashed `3 3` keeps it the same weight as its siblings.
            */}
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="atMs"
              domain={[startMs, endMs]}
              ticks={axisTicks(startMs, endMs)}
              tickFormatter={formatInstant}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              // The domain is the window and the ticks are computed from it, so Recharts must not
              // widen either to reach a rounder number: a padded axis would draw marks outside the
              // window the caption claims.
              allowDataOverflow={false}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              type="number"
              dataKey="amount"
              domain={[0, 'auto']}
              tickFormatter={(value: number) => axisAmountLabel(value, currency)}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={52}
            />
            {/* Fixes every mark at one size: this is a two-variable plot, not a bubble chart. */}
            <ZAxis type="number" range={[MARK_AREA, MARK_AREA]} />
            <RechartsTooltip
              content={<ScatterTooltip currency={currency} labels={labels} />}
              cursor={{ stroke: 'hsl(var(--border))', strokeDasharray: '3 3' }}
            />
            <Scatter
              data={marks}
              name={labels.amount}
              fill="hsl(var(--chart-1))"
              // Translucent on purpose — stacked marks darken, which is the density reading. See
              // rule 2 in the module comment.
              fillOpacity={0.45}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      {/* Text equivalent — the chart is decorative once this table exists. */}
      <ScatterTable slices={slices} currency={currency} description={description} labels={labels} />
    </figure>
  );
}
